"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export interface DropdownMenuItem {
  key: string;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
  icon?: React.ReactNode;
  shortcut?: string;
}

export interface DropdownMenuV2Props {
  trigger: React.ReactNode;
  items: DropdownMenuItem[];
  align?: "start" | "end";
  width?: number | string;
  className?: string;
}

export function DropdownMenuV2({
  trigger,
  items,
  align = "end",
  width,
  className,
}: DropdownMenuV2Props) {
  const [open, setOpen] = React.useState(false);
  const [highlightIdx, setHighlightIdx] = React.useState(-1);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const uid = React.useId();
  const menuId = `${uid}-menu`;

  const enabledIndices = React.useMemo(
    () => items.map((it, i) => (it.disabled ? -1 : i)).filter((i) => i >= 0),
    [items]
  );

  // Click outside
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Focus menu container + highlight first item on open
  React.useEffect(() => {
    if (open) {
      setHighlightIdx(enabledIndices[0] ?? -1);
      // Defer focus to next frame so DOM is mounted
      requestAnimationFrame(() => {
        menuRef.current?.focus();
      });
    }
  }, [open, enabledIndices]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const selectItem = (item: DropdownMenuItem) => {
    if (item.disabled) return;
    item.onSelect();
    close();
  };

  const moveHighlight = (dir: 1 | -1) => {
    const pos = enabledIndices.indexOf(highlightIdx);
    let next: number;
    if (dir === 1) {
      next =
        pos < enabledIndices.length - 1
          ? enabledIndices[pos + 1]
          : enabledIndices[0];
    } else {
      next =
        pos > 0
          ? enabledIndices[pos - 1]
          : enabledIndices[enabledIndices.length - 1];
    }
    setHighlightIdx(next);
  };

  const handleTriggerKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  };

  const handleMenuKey = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveHighlight(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveHighlight(-1);
        break;
      case "Home":
        e.preventDefault();
        setHighlightIdx(enabledIndices[0] ?? -1);
        break;
      case "End":
        e.preventDefault();
        setHighlightIdx(enabledIndices[enabledIndices.length - 1] ?? -1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (highlightIdx >= 0 && items[highlightIdx]) {
          selectItem(items[highlightIdx]);
        }
        break;
      case "Escape":
        e.preventDefault();
        close();
        break;
    }
  };

  // Scroll highlighted into view
  React.useEffect(() => {
    if (highlightIdx >= 0 && menuRef.current) {
      const el = menuRef.current.children[highlightIdx] as
        | HTMLElement
        | undefined;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIdx]);

  return (
    <div ref={containerRef} className={cn("relative inline-block", className)}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={handleTriggerKey}
        className="v2-focus-ring inline-flex items-center"
      >
        {trigger}
      </button>

      {/* Menu */}
      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="Eylemler"
          tabIndex={-1}
          onKeyDown={handleMenuKey}
          className={cn(
            "absolute z-50 mt-1 max-h-64 overflow-y-auto rounded-[var(--v2-radius-md)] border border-[var(--v2-border)] bg-white py-1 shadow-lg animate-in fade-in zoom-in-95 duration-100",
            align === "end" ? "right-0" : "left-0"
          )}
          style={{ width: width ?? "auto", minWidth: 180 }}
        >
          {items.map((item, idx) => {
            const highlighted = idx === highlightIdx;
            return (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                tabIndex={-1}
                disabled={item.disabled}
                onClick={() => selectItem(item)}
                onMouseEnter={() => !item.disabled && setHighlightIdx(idx)}
                className={cn(
                  "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors",
                  highlighted &&
                    !item.disabled &&
                    "bg-[var(--v2-accent-bg,#eff6ff)]",
                  item.danger
                    ? "text-red-600 hover:bg-red-50"
                    : "text-[var(--v2-text-strong)]",
                  item.disabled && "cursor-not-allowed opacity-40",
                  !highlighted &&
                    !item.disabled &&
                    !item.danger &&
                    "hover:bg-slate-50"
                )}
              >
                {item.icon && (
                  <span className="shrink-0 text-base" aria-hidden="true">
                    {item.icon}
                  </span>
                )}
                <span className="flex-1">{item.label}</span>
                {item.shortcut && (
                  <kbd className="ml-auto text-[10px] tracking-wide text-[var(--v2-text-muted)]">
                    {item.shortcut}
                  </kbd>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
